/**
 * Cross-Entity Composition — Sprint 17f-1 Task 1
 *
 * Single LLM call: given all entities + user context, return the best
 * cross-entity compositions. The LLM decides which pairs (if any) make sense.
 * No LLM = no compositions (returns []).
 */
import { recordIkigaiRouteLocalOutcome } from './route-local-outcome';
// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
export const COMPOSITION_CONFIG = {
    maxCompositions: 5,
    llmTimeout: 15000,
    llmMaxTokens: 500,
};
const VALID_AGENT_TYPES = new Set([
    'shopping',
    'restaurant',
    'travel',
    'events',
    'content',
]);
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Single LLM call: given all entities + user context, return the best
 * cross-entity compositions. The LLM decides which pairs (if any) make sense.
 * No LLM = no compositions (returns []).
 */
export async function composeEntities(entities, context, llm, modelPrefs) {
    if (!llm || entities.length < 2)
        return [];
    let outcome;
    try {
        const model = modelPrefs?.entityComposition ?? modelPrefs?.fast;
        if (!model) {
            console.warn('[EntityComposer] No entity composition model configured — skipping composition');
            outcome = {
                operation: 'entityComposition',
                status: 'not_run',
                result: 'skipped',
            };
            return [];
        }
        const now = Date.now();
        const entitySummaries = entities.map(e => ({
            name: e.name,
            category: e.category,
            type: e.type,
            confidence: e.confidence,
            trend: e.trend,
            topSignals: (e.signals ?? [])
                .sort((a, b) => b.strength - a.strength)
                .slice(0, 2)
                .map(s => s.summary),
        }));
        const prompt = `You are deciding which of this user's interests combine naturally into cross-interest missions.

USER PROFILE:
${context.ikigaiSummary ?? 'No profile available yet.'}

THEIR INTERESTS:
${JSON.stringify(entitySummaries, null, 2)}

FEEDBACK HISTORY:
- Loved: ${(context.lovedMissions ?? []).join(', ') || 'none yet'}
- Dismissed: ${(context.dismissedMissions ?? []).join(', ') || 'none yet'}
- Completed: ${(context.completedMissions ?? []).join(', ') || 'none yet'}

Which interests combine naturally for THIS person? Consider:
- Do these interests overlap in their daily life?
- Would combining them create something the user would actually do?
- Does their feedback history suggest they'd respond to this type of combination?

Return ONLY a JSON array. Each item:
{
  "entityNames": ["Interest A", "Interest B"],
  "description": "Specific, actionable combined mission",
  "agentType": "shopping|restaurant|travel|events|content",
  "reasoning": "Why this combination makes sense for this specific user"
}

Return [] if no combinations make natural sense. Do NOT force combinations.
- Only combine interests with a plausible real-world connection.
- Interests from completely unrelated domains (e.g., "crypto trading" + "golf") must NEVER be combined.
- Test: would a real person naturally do both in a single activity, outing, or purchase?
- When in doubt, return []. False negatives are better than nonsensical combinations.`;
        let response;
        try {
            response = await llm.complete(prompt, {
                model,
                operation: 'entityComposition',
                timeout: COMPOSITION_CONFIG.llmTimeout,
                maxTokens: COMPOSITION_CONFIG.llmMaxTokens,
            });
        }
        catch {
            outcome = {
                operation: 'entityComposition',
                status: 'not_run',
                result: 'no_op',
                errorCode: 'provider_error',
            };
            return [];
        }
        try {
            const jsonMatch = response.text.match(/\[[\s\S]*\]/);
            if (!jsonMatch) {
                outcome = {
                    operation: 'entityComposition',
                    status: 'parse_failed',
                    result: 'no_op',
                    errorCode: 'parse_failed',
                };
                return [];
            }
            const parsed = JSON.parse(jsonMatch[0]);
            if (!Array.isArray(parsed) || parsed.length === 0) {
                outcome = {
                    operation: 'entityComposition',
                    status: 'success',
                    result: 'no_op',
                };
                return [];
            }
            const opportunities = [];
            for (const item of parsed.slice(0, COMPOSITION_CONFIG.maxCompositions)) {
                if (!item.description || !item.reasoning)
                    continue;
                if (!VALID_AGENT_TYPES.has(item.agentType))
                    continue;
                if (!Array.isArray(item.entityNames) || item.entityNames.length < 2)
                    continue;
                const compositeEntities = item.entityNames.join(', ');
                opportunities.push({
                    entityName: item.entityNames.join(' + '),
                    description: item.description,
                    agentType: item.agentType,
                    suggestedMission: item.description,
                    context: {
                        source: 'composite',
                        compositeEntities,
                        compositionReasoning: item.reasoning,
                        facet: `${item.entityNames.join('+')}:${item.agentType}`,
                    },
                    createdAt: now,
                    status: 'pending',
                });
            }
            outcome = {
                operation: 'entityComposition',
                status: 'success',
                result: opportunities.length > 0 ? 'success' : 'no_op',
            };
            return opportunities;
        }
        catch {
            outcome = {
                operation: 'entityComposition',
                status: 'parse_failed',
                result: 'no_op',
                errorCode: 'parse_failed',
            };
            return [];
        }
    }
    finally {
        recordIkigaiRouteLocalOutcome(llm, outcome);
    }
}
//# sourceMappingURL=entity-composer.js.map